import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import * as adminService from '../../services/adminService';
import { parseBackendDate } from '../../utils/parseBackendDate';
import './AdminSubscriptionDetailPage.css';

interface SubscriptionUser {
  email: string;
  username: string;
  avatarUrl: string | null;
  role: string;
  createdAt: string;
  lastLoginAt: string | null;
}

interface SubscriptionDetail {
  id: string;
  userId: string;
  status: string;
  platform: string;
  startedAt: string;
  expiresAt: string;
  cancelledAt: string | null;
  trialEndsAt: string | null;
  stripeSubscriptionId: string | null;
  user: SubscriptionUser;
  product: { name: string; billingPeriod: string };
}

interface Transaction {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  transactionId: string;
}

interface ModalState {
  type: 'extend' | 'grant' | 'revoke' | null;
  loading: boolean;
}

const AdminSubscriptionDetailPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [modal, setModal] = useState<ModalState>({ type: null, loading: false });
  const [modalDays, setModalDays] = useState(7);
  const [modalReason, setModalReason] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.getSubscriptionDetail(id);
      setSubscription(result.subscription);
      setTransactions(result.transactions || []);
    } catch {
      setError(t('subscriptionDetail.fetchError', 'Failed to load subscription details'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const formatDate = (dateStr: string | null): string => {
    const d = parseBackendDate(dateStr);
    if (!d) return '-';
    return d.toLocaleDateString('en', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateStr: string): string => {
    const d = parseBackendDate(dateStr);
    if (!d) return '-';
    return d.toLocaleDateString('en', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusClass = (s: string): string => {
    switch (s.toLowerCase()) {
      case 'active': return 'admin-sub-detail-badge--active';
      case 'trialing': return 'admin-sub-detail-badge--trialing';
      case 'cancelled': return 'admin-sub-detail-badge--cancelled';
      case 'succeeded':
      case 'paid': return 'admin-sub-detail-badge--active';
      case 'failed':
      case 'refunded': return 'admin-sub-detail-badge--cancelled';
      default: return 'admin-sub-detail-badge--inactive';
    }
  };

  const handleCopyStripeId = async () => {
    if (!subscription?.stripeSubscriptionId) return;
    try {
      await navigator.clipboard.writeText(subscription.stripeSubscriptionId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  const openModal = (type: 'extend' | 'grant' | 'revoke') => {
    setModal({ type, loading: false });
    setModalDays(7);
    setModalReason('');
  };

  const closeModal = () => {
    if (modal.loading) return;
    setModal({ type: null, loading: false });
  };

  const handleModalConfirm = async () => {
    if (!id || !subscription) return;
    setModal((prev) => ({ ...prev, loading: true }));

    try {
      if (modal.type === 'extend') {
        await adminService.extendTrial(id, modalDays);
        setToast({ message: t('subscriptionDetail.extendSuccess', 'Trial extended successfully'), type: 'success' });
      } else if (modal.type === 'grant') {
        await adminService.grantPremium(subscription.userId, { days: modalDays, reason: modalReason });
        setToast({ message: t('subscriptionDetail.grantSuccess', 'Premium granted successfully'), type: 'success' });
      } else if (modal.type === 'revoke') {
        await adminService.revokeSubscription(id, modalReason);
        setToast({ message: t('subscriptionDetail.revokeSuccess', 'Subscription revoked'), type: 'success' });
      }
      closeModal();
      fetchData();
    } catch {
      setToast({
        message: t('subscriptionDetail.actionError', 'Action failed. Please try again.'),
        type: 'error',
      });
      setModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const getModalTitle = (): string => {
    switch (modal.type) {
      case 'extend': return t('subscriptionDetail.extendTrialTitle', 'Extend Trial');
      case 'grant': return t('subscriptionDetail.grantPremiumTitle', 'Grant Premium');
      case 'revoke': return t('subscriptionDetail.revokeTitle', 'Revoke Subscription');
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="admin-sub-detail-page">
        <div className="admin-sub-detail-skeleton-header" />
        <div className="admin-sub-detail-columns">
          <div className="admin-sub-detail-skeleton-card" />
          <div className="admin-sub-detail-skeleton-card" />
        </div>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="admin-sub-detail-page">
        <button className="admin-sub-detail-back" onClick={() => navigate('/admin/subscriptions')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t('subscriptionDetail.back', 'Back to Subscriptions')}
        </button>
        <div className="admin-sub-detail-error">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p>{error || t('subscriptionDetail.notFound', 'Subscription not found')}</p>
          <button className="admin-sub-detail-retry-btn" onClick={fetchData}>
            {t('subscriptionDetail.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-sub-detail-page">
      {/* Back button */}
      <button className="admin-sub-detail-back" onClick={() => navigate('/admin/subscriptions')}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        {t('subscriptionDetail.back', 'Back to Subscriptions')}
      </button>

      {/* Two-column layout */}
      <div className="admin-sub-detail-columns">
        {/* Left column */}
        <div className="admin-sub-detail-left">
          {/* User Info Card */}
          <div className="admin-sub-detail-card">
            <h2 className="admin-sub-detail-card-title">
              {t('subscriptionDetail.userInfo', 'User Info')}
            </h2>
            <div className="admin-sub-detail-user-header">
              <div className="admin-sub-detail-avatar">
                {subscription.user.avatarUrl ? (
                  <img src={subscription.user.avatarUrl} alt="" />
                ) : (
                  <span>
                    {(subscription.user.username || subscription.user.email).charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="admin-sub-detail-user-main">
                <span className="admin-sub-detail-user-name">
                  {subscription.user.username || '-'}
                </span>
                <span className="admin-sub-detail-user-email">
                  {subscription.user.email}
                </span>
              </div>
            </div>
            <div className="admin-sub-detail-info-grid">
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.role', 'Role')}
                </span>
                <span className={`admin-sub-detail-role-badge admin-sub-detail-role--${subscription.user.role}`}>
                  {subscription.user.role}
                </span>
              </div>
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.joined', 'Joined')}
                </span>
                <span className="admin-sub-detail-info-value">
                  {formatShortDate(subscription.user.createdAt)}
                </span>
              </div>
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.lastLogin', 'Last Login')}
                </span>
                <span className="admin-sub-detail-info-value">
                  {formatDate(subscription.user.lastLoginAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Subscription Card */}
          <div className="admin-sub-detail-card">
            <h2 className="admin-sub-detail-card-title">
              {t('subscriptionDetail.subscription', 'Subscription')}
            </h2>
            <div className="admin-sub-detail-info-grid">
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.statusLabel', 'Status')}
                </span>
                <span className={`admin-sub-detail-badge ${getStatusClass(subscription.status)}`}>
                  {subscription.status}
                </span>
              </div>
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.planName', 'Plan')}
                </span>
                <span className="admin-sub-detail-info-value">
                  {subscription.product?.name || '-'}
                </span>
              </div>
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.billingPeriod', 'Billing Period')}
                </span>
                <span className="admin-sub-detail-info-value">
                  {subscription.product?.billingPeriod || '-'}
                </span>
              </div>
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.platformLabel', 'Platform')}
                </span>
                <span className="admin-sub-detail-info-value admin-sub-detail-platform">
                  {subscription.platform || '-'}
                </span>
              </div>
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.started', 'Started')}
                </span>
                <span className="admin-sub-detail-info-value">
                  {formatDate(subscription.startedAt)}
                </span>
              </div>
              <div className="admin-sub-detail-info-row">
                <span className="admin-sub-detail-info-label">
                  {t('subscriptionDetail.expires', 'Expires')}
                </span>
                <span className="admin-sub-detail-info-value">
                  {formatDate(subscription.expiresAt)}
                </span>
              </div>
              {subscription.trialEndsAt && (
                <div className="admin-sub-detail-info-row">
                  <span className="admin-sub-detail-info-label">
                    {t('subscriptionDetail.trialEnds', 'Trial Ends')}
                  </span>
                  <span className="admin-sub-detail-info-value">
                    {formatDate(subscription.trialEndsAt)}
                  </span>
                </div>
              )}
              {subscription.stripeSubscriptionId && (
                <div className="admin-sub-detail-info-row">
                  <span className="admin-sub-detail-info-label">
                    {t('subscriptionDetail.stripeId', 'Stripe ID')}
                  </span>
                  <div className="admin-sub-detail-copyable">
                    <code className="admin-sub-detail-code">
                      {subscription.stripeSubscriptionId}
                    </code>
                    <button
                      className="admin-sub-detail-copy-btn"
                      onClick={handleCopyStripeId}
                      title={t('subscriptionDetail.copy', 'Copy')}
                    >
                      {copied ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="admin-sub-detail-right">
          {/* Payment History */}
          <div className="admin-sub-detail-card">
            <h2 className="admin-sub-detail-card-title">
              {t('subscriptionDetail.paymentHistory', 'Payment History')}
            </h2>
            {transactions.length > 0 ? (
              <div className="admin-sub-detail-tx-wrapper">
                <table className="admin-sub-detail-tx-table">
                  <thead>
                    <tr>
                      <th>{t('subscriptionDetail.txDate', 'Date')}</th>
                      <th>{t('subscriptionDetail.txAmount', 'Amount')}</th>
                      <th>{t('subscriptionDetail.txStatus', 'Status')}</th>
                      <th>{t('subscriptionDetail.txProvider', 'Provider')}</th>
                      <th>{t('subscriptionDetail.txId', 'Transaction ID')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td className="admin-sub-detail-tx-date">
                          {formatShortDate(tx.date)}
                        </td>
                        <td className="admin-sub-detail-tx-amount">
                          {tx.currency?.toUpperCase()} {tx.amount.toFixed(2)}
                        </td>
                        <td>
                          <span className={`admin-sub-detail-badge ${getStatusClass(tx.status)}`}>
                            {tx.status}
                          </span>
                        </td>
                        <td className="admin-sub-detail-tx-provider">{tx.provider}</td>
                        <td className="admin-sub-detail-tx-id">
                          <code>{tx.transactionId}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-sub-detail-tx-empty">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                <p>{t('subscriptionDetail.noPayments', 'No payment history')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="admin-sub-detail-actions">
        <button
          className="admin-sub-detail-action-btn admin-sub-detail-action--extend"
          onClick={() => openModal('extend')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {t('subscriptionDetail.extendTrial', 'Extend Trial')}
        </button>
        <button
          className="admin-sub-detail-action-btn admin-sub-detail-action--grant"
          onClick={() => openModal('grant')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          {t('subscriptionDetail.grantPremium', 'Grant Premium')}
        </button>
        <button
          className="admin-sub-detail-action-btn admin-sub-detail-action--revoke"
          onClick={() => openModal('revoke')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {t('subscriptionDetail.revokeSubscription', 'Revoke Subscription')}
        </button>
      </div>

      {/* Modal */}
      {modal.type && (
        <div className="admin-sub-detail-overlay" onClick={closeModal}>
          <div
            className="admin-sub-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="admin-sub-detail-modal-title">{getModalTitle()}</h3>

            {(modal.type === 'extend' || modal.type === 'grant') && (
              <div className="admin-sub-detail-modal-field">
                <label className="admin-sub-detail-modal-label">
                  {t('subscriptionDetail.daysLabel', 'Number of days')}
                </label>
                <input
                  type="number"
                  className="admin-sub-detail-modal-input"
                  value={modalDays}
                  onChange={(e) => setModalDays(Math.max(1, Math.min(90, Number(e.target.value))))}
                  min={1}
                  max={90}
                />
              </div>
            )}

            {(modal.type === 'grant' || modal.type === 'revoke') && (
              <div className="admin-sub-detail-modal-field">
                <label className="admin-sub-detail-modal-label">
                  {t('subscriptionDetail.reasonLabel', 'Reason')}
                </label>
                <textarea
                  className="admin-sub-detail-modal-textarea"
                  value={modalReason}
                  onChange={(e) => setModalReason(e.target.value)}
                  rows={3}
                  placeholder={t('subscriptionDetail.reasonPlaceholder', 'Enter reason...')}
                />
              </div>
            )}

            {modal.type === 'revoke' && (
              <p className="admin-sub-detail-modal-warning">
                {t('subscriptionDetail.revokeWarning', 'This action cannot be undone. The user will lose access immediately.')}
              </p>
            )}

            <div className="admin-sub-detail-modal-actions">
              <button
                className="admin-sub-detail-modal-cancel"
                onClick={closeModal}
                disabled={modal.loading}
              >
                {t('subscriptionDetail.cancel', 'Cancel')}
              </button>
              <button
                className={`admin-sub-detail-modal-confirm ${
                  modal.type === 'revoke' ? 'admin-sub-detail-modal-confirm--destructive' : ''
                }`}
                onClick={handleModalConfirm}
                disabled={
                  modal.loading ||
                  ((modal.type === 'revoke' || modal.type === 'grant') && modalReason.trim().length < 3)
                }
              >
                {modal.loading ? (
                  <span className="admin-sub-detail-spinner" />
                ) : (
                  t('subscriptionDetail.confirm', 'Confirm')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`admin-sub-detail-toast admin-sub-detail-toast--${toast.type}`}>
          {toast.type === 'success' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionDetailPage;
